import { useEffect, useState, useMemo } from "react";
import { useWallet } from "@/context/WalletContext";
import {
  getStats, getLocks, getUnlockSchedule, getNextUnlock, getActivity,
  fmtUsd, fmtNum, shortAddr,
} from "@/lib/api";
import { StatCard, StatusBadge, ProductTag } from "@/dashboard/ui";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Activity, ArrowRight } from "lucide-react";

function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!target) return <span className="font-heading text-2xl text-welock-textMuted">--</span>;
  const diff = Math.max(0, new Date(target).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const seg = (v, l) => (
    <span className="flex items-baseline gap-1">
      <span className="font-heading text-2xl font-medium tracking-tight text-welock-accent md:text-3xl">
        {String(v).padStart(2, "0")}
      </span>
      <span className="font-mono text-[10px] uppercase text-welock-textMuted">{l}</span>
    </span>
  );
  return (
    <div className="flex flex-wrap items-baseline gap-4">
      {seg(d, "d")}{seg(h, "h")}{seg(m, "m")}{seg(s, "s")}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-welock-border bg-welock-surface px-3 py-2 font-mono text-xs">
      <div className="text-welock-textMuted">{label}</div>
      <div className="text-welock-accent">{fmtUsd(payload[0].value)}</div>
    </div>
  );
}

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function Overview({ onNavigate }) {
  const { address } = useWallet();
  const [stats, setStats] = useState(null);
  const [locks, setLocks] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [next, setNext] = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    getStats().then(setStats).catch(() => {});
    getLocks(address).then(setLocks).catch(() => {});
    getUnlockSchedule(address).then(setSchedule).catch(() => {});
    getNextUnlock(address).then(setNext).catch(() => {});
    getActivity(address).then(setActivity).catch(() => {});
  }, [address]);

  const myTvl = useMemo(
    () => locks.filter((l) => l.status === "active").reduce((a, l) => a + l.value_usd, 0),
    [locks]
  );

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="stat-tvl" label="Protocol TVL" value={stats ? fmtUsd(stats.tvl) : "--"} sub="Across all active locks" accent />
        <StatCard testId="stat-active" label="Active Locks" value={stats ? fmtNum(stats.active_locks) : "--"} sub={`${locks.length} in your view`} />
        <StatCard testId="stat-your-value" label="Your Locked Value" value={fmtUsd(myTvl)} sub="Active positions" />
        <div className="border border-welock-border bg-welock-surface p-6" data-testid="stat-next-unlock">
          <div className="font-mono text-[11px] uppercase tracking-widest text-welock-textMuted">Next Unlock</div>
          <div className="mt-3"><Countdown target={next?.has_next ? next.unlock_date : null} /></div>
          <div className="mt-2 font-mono text-xs text-welock-textSecondary truncate">
            {next?.has_next ? next.title : "No upcoming unlocks"}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="border border-welock-border bg-welock-surface p-6" data-testid="unlock-schedule-chart">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <span className="eyebrow">unlock schedule</span>
            <h3 className="mt-2 font-heading text-lg text-welock-textPrimary">Value unlocking over 12 months</h3>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={schedule} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00DC82" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#00DC82" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1E222D" vertical={false} />
              <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#1E222D" }} />
              <YAxis stroke="#6B7280" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} tickFormatter={(v) => fmtUsd(v)} width={60} />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#262C3A" }} />
              <Area type="monotone" dataKey="value" stroke="#00DC82" strokeWidth={1.5} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        {/* Locks table */}
        <div className="lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg text-welock-textPrimary">Your locks</h3>
            <button data-testid="view-portfolio" onClick={() => onNavigate("portfolio")} className="flex items-center gap-1.5 font-mono text-xs text-welock-textSecondary hover:text-welock-accent">
              View portfolio <ArrowRight size={12} />
            </button>
          </div>
          <div className="border border-welock-border bg-welock-surface">
            <div className="hidden grid-cols-12 gap-2 border-b border-welock-border px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-welock-textMuted md:grid">
              <div className="col-span-4">Asset</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-3 text-right">Value</div>
              <div className="col-span-3 text-right">Status</div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {locks.slice(0, 12).map((l) => (
                <div key={l.id} data-testid={`lock-row-${l.id}`} className="grid grid-cols-12 items-center gap-2 border-b border-welock-border/60 px-4 py-3 last:border-0 hover:bg-welock-surfaceHover">
                  <div className="col-span-6 md:col-span-4">
                    <div className="text-sm text-welock-textPrimary">{l.token_symbol}</div>
                    <div className="font-mono text-[11px] text-welock-textMuted">{fmtNum(l.amount)}</div>
                  </div>
                  <div className="col-span-2 hidden md:block"><ProductTag product={l.product} /></div>
                  <div className="col-span-3 hidden text-right font-mono text-sm text-welock-textPrimary md:block">{fmtUsd(l.value_usd)}</div>
                  <div className="col-span-6 flex justify-end md:col-span-3"><StatusBadge status={l.status} /></div>
                </div>
              ))}
              {locks.length === 0 && (
                <div className="px-4 py-10 text-center font-mono text-xs text-welock-textMuted">No locks yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Activity */}
        <div>
          <h3 className="mb-4 flex items-center gap-2 font-heading text-lg text-welock-textPrimary">
            <Activity size={16} className="text-welock-accent" /> Recent activity
          </h3>
          <div className="space-y-px border border-welock-border bg-welock-border" data-testid="activity-feed">
            {activity.slice(0, 10).map((a) => (
              <div key={a.id} className="bg-welock-surface px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-welock-textSecondary">{shortAddr(a.wallet)}</span>
                  <span className="font-mono text-[10px] text-welock-textMuted">{timeAgo(a.timestamp)}</span>
                </div>
                <div className="mt-1 text-sm text-welock-textPrimary">
                  {a.action} <span className="text-welock-accent">{a.token_symbol}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
